import { useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import type { Attachment } from '../../client'
import Spinner from '../chat/Spinner'
import AttachmentPreview from './AttachmentPreview'
import MediaPreview from './MediaPreview'
import type { ComposerUploads } from './useComposerAttachments'

type Props = {
  uploads: ComposerUploads
  compact?: boolean
}

export default function ComposerAttachments({ uploads, compact = false }: Props) {
  const [preview, setPreview] = useState<Attachment | null>(null)

  if (uploads.items.length === 0) return null

  return (
    <>
      <ul className={`flex flex-wrap gap-2 ${compact ? 'px-4 pt-3' : 'px-5 pt-4'}`}>
        {uploads.items.map(({ id, shown, attachment, failed }) => (
          <li key={id} className="relative">
            <button
              type="button"
              disabled={!attachment}
              onClick={() =>
                attachment?.kind === 'file'
                  ? window.open(attachment.url, '_blank', 'noopener,noreferrer')
                  : setPreview(attachment)
              }
              title={shown.original_filename ?? undefined}
              aria-label={`Preview ${shown.original_filename ?? 'attachment'}`}
              className={`relative block overflow-hidden rounded-md not-disabled:cursor-zoom-in ${
                compact ? 'size-12' : 'size-14'
              } ${failed ? 'opacity-50' : ''}`}
            >
              <AttachmentPreview attachment={shown} />
              {!attachment && !failed && (
                <span className="absolute inset-0 flex items-center justify-center bg-white/60">
                  <Spinner className="size-4 border-purple-200 border-t-purple-600" />
                </span>
              )}
            </button>
            <button
              type="button"
              aria-label={`Remove ${shown.original_filename ?? 'attachment'}`}
              onClick={() => uploads.remove(id)}
              className={`absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full text-white ${
                failed ? 'bg-red-600' : 'bg-stone-900'
              }`}
            >
              <XMarkIcon className="size-3" strokeWidth={2.4} />
            </button>
          </li>
        ))}
      </ul>

      <MediaPreview
        src={preview?.url ?? null}
        kind={preview?.kind}
        alt={preview?.original_filename ?? ''}
        onClose={() => setPreview(null)}
      />
    </>
  )
}
